/**
 * <lp-sticky-mobile-cta>
 *
 * Bottom-fixed mobile CTA bar that smooth-scrolls to the parent buy-box
 * section on tap. Visibility requires both:
 *   - user has scrolled past ~70vh (hero passed)
 *   - target buy box is not in the viewport
 *
 * Default state is hidden via CSS transform translateY(100%) and
 * aria-hidden="true" to prevent stale screen-reader focus.
 *
 * Cleanup via AbortController. No innerHTML mutations.
 */
(() => {
  if (customElements.get('lp-sticky-mobile-cta')) return;

  class LpStickyMobileCta extends HTMLElement {
    #abortController = null;
    #target = null;
    #buyBoxVisible = false;
    #heroPassed = false;
    #observer = null;
    #rafId = null;
    #prefersReducedMotion = false;

    connectedCallback() {
      // Reparent to <body> on first connect so position: fixed pins to the
      // viewport, not an ancestor with transform/perspective/filter/contain
      // (e.g. .figma-max-landing-page, .foundry-aigen-product, or any
      // animated section above us). Re-connection runs the rest of setup.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }

      const targetId = this.dataset.targetId;
      if (!targetId) return;

      this.#target = document.getElementById('shopify-section-' + targetId)
        || document.getElementById(targetId);
      if (!this.#target) return;

      this.#prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.#abortController = new AbortController();

      this.#observer = new IntersectionObserver(this.#handleIntersect, { threshold: 0.5 });
      this.#observer.observe(this.#target);

      const { signal } = this.#abortController;
      window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });

      const button = this.querySelector('[data-lp-sticky-cta-button]');
      if (button) button.addEventListener('click', this.#handleClick, { signal });

      this.#updateHeroPassed();
    }

    disconnectedCallback() {
      if (this.#abortController) this.#abortController.abort();
      if (this.#observer) this.#observer.disconnect();
      if (this.#rafId) cancelAnimationFrame(this.#rafId);
    }

    #handleIntersect = (entries) => {
      for (const entry of entries) {
        this.#buyBoxVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      }
      this.#render();
    };

    #handleScroll = () => {
      if (this.#rafId) return;
      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = null;
        this.#updateHeroPassed();
      });
    };

    #updateHeroPassed() {
      const passed = window.scrollY > window.innerHeight * 0.7;
      if (passed === this.#heroPassed) return;
      this.#heroPassed = passed;
      this.#render();
    }

    #render() {
      const shouldShow = this.#heroPassed && !this.#buyBoxVisible;
      if (shouldShow) {
        this.setAttribute('data-visible', 'true');
        this.removeAttribute('aria-hidden');
      } else {
        this.removeAttribute('data-visible');
        this.setAttribute('aria-hidden', 'true');
      }
    }

    #handleClick = (event) => {
      event.preventDefault();
      if (!this.#target) return;

      this.#target.scrollIntoView({
        behavior: this.#prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });

      const focusTarget = this.#target.querySelector('variant-picker, [data-fap-atc], [data-figma-buy-box-atc], button')
        || this.#target;
      if (!focusTarget.hasAttribute('tabindex')) {
        focusTarget.setAttribute('tabindex', '-1');
      }
      window.setTimeout(() => {
        focusTarget.focus({ preventScroll: true });
      }, this.#prefersReducedMotion ? 0 : 400);
    };
  }

  customElements.define('lp-sticky-mobile-cta', LpStickyMobileCta);
})();
